/**
 * The portrait contract, held where `src/data` is allowed to see it.
 *
 * ── Why this file exists twice ──────────────────────────────────────────────
 *
 * The authority for these two records is `src/ui/racefaces.ts`, written by the
 * branch that draws them. Every word of the reasoning is there and it is worth
 * reading before filling one of these in; what follows is the shape only.
 *
 * It cannot simply be imported. `tests/layering.test.ts` and an ESLint rule
 * forbid `src/data` from importing anything under `src/ui`, and they are right
 * to: races are content and content may not depend on a renderer. So the shape
 * is mirrored here, field for field and name for name, and the two are bridged
 * by **structural typing** — `const f: RaceFace = race('litany').face` compiles
 * in the UI with no cast and no adapter, because they are the same shape.
 *
 * The merge should collapse these into one file under `src/data` and have
 * `racefaces.ts` re-export it. Until then, the rule is: **`src/ui/racefaces.ts`
 * is the authority and this file follows it.** If they drift, this one is wrong.
 *
 * ── The one rule that decides whether the roster works ──────────────────────
 *
 * `RaceFace` is the **body** and `HouseLook` is the **culture**, and nothing may
 * cross. A race entry that says "wears scrap iron" has spent the only lever the
 * second kingdom of that race had, and the two goblin kingdoms the owner asked
 * for become one kingdom in two colours. Bone in `RaceFace`; everything worn,
 * cut, forged or held in `HouseLook`.
 *
 * ── Two sizes, and which fields work at which ───────────────────────────────
 *
 * Learned the expensive way, from the portrait branch measuring rather than
 * reasoning. These records are read at two sizes and **the fields do not carry
 * equally at both**, so "are these two houses different enough" is not one
 * question:
 *
 *   **46px, dimmed, in a vertical rail** — the selection screen, where
 *   twenty-one of twenty-two rows are muted at any moment. Only the *outline*
 *   survives: `crown`, `ear`, `crest`, `frameScale` and the value of the skin
 *   ramp against the niche. Everything else is invisible.
 *
 *   **248px, lit, two houses side by side** — the hero shot, which `newgame`
 *   reaches from a "the other kingdom of X" link, so two houses of one race are
 *   one click apart at full size. Here `mantle`, `material` and `mark` do the
 *   work, and the modelling inside the silhouette — `faceLength`, `cheekHeight`,
 *   `cheek`, `lid` — finally shows.
 *
 * The trap this is written to prevent, which nearly cost an afternoon: widening
 * `faceLength` or `cheekHeight` to separate four races on the *rail* buys
 * literally nothing, because both are full-size levers and the rail shows no
 * modelling at all. Optimising a real quality against the wrong size is the same
 * mistake as ignoring it.
 *
 * `housesDiffer` is unchanged and three of five still holds — this says *which*
 * three you want when a pair's hard case is the rail rather than the hero shot.
 *
 * ── The enum is a contract with two signatories, and one of them compiles ───
 *
 * A value being in one of these unions means the data may *name* it. It does not
 * mean anything draws it. `hood` was in the vocabulary, typechecked, passed every
 * assertion here, and had no case in `crownArt` at all — it fell through to the
 * default, which is `antler-rack`, so the one house wearing a hood would have
 * rendered in a rack of antlers.
 *
 * So: **adding a value to one of these unions is half a change.** The other half
 * is a case in the renderer, and nothing in this directory can check it. The rule
 * agreed with the portrait branch is that a value is added *when a house needs
 * it* and rendered in the same pass, so it is proven on first use — never
 * speculatively. If you add one and have not had it confirmed drawn, assume it
 * is not.
 */

/* ── Layer one: the body ─────────────────────────────────────────────────── */

/** A people's face, as multipliers on a human one. 1 is human throughout. */
export interface RaceFace {
  id: string;

  // The skull. `cranium` and `jaw` do the most work and are worth setting in
  // opposition — a big vault over a small jaw and a small vault over a heavy one
  // are two silhouettes nobody confuses, and either can be a goblin.
  cranium: number;
  temple: number;
  cheek: number;
  /**
   * Where the cheekbone sits between eye and jaw. 0 high and gaunt, 1 low and
   * soft.
   *
   * **Safe at both ends, and it was not always.** This field twice broke a
   * drawing assumption at its low end: ears were anchored to the cheekbone, and
   * a gaunt face puts the cheekbone above the eyeline, so the Silver Reaches'
   * ears grew out of the corner of the eye and read across the face. The fix
   * settled into two rules that between them mean **nothing reads
   * `cheekHeight` for a vertical position any more** — an elf's ear hangs from
   * the eyeline, a beast's `tufted` ear rides the skull. A new race may put this
   * anywhere in its range.
   *
   * Kept as a note rather than deleted because it is the worked example for the
   * rule above it: the roster's true low holder was `litany` at 0.20, not the
   * `silver` at 0.22 that prompted the fix, and it only failed to bite there
   * because the Litany have no ears at all. Luck is not a fix.
   */
  cheekHeight: number;
  /** Above ~1.3 it must exceed `temple` or the head reads as a pear. */
  jaw: number;
  /** Equal to `jaw` makes a brick. */
  chin: number;
  faceLength: number;
  /** 0 none, 1 human, 2 sinks the eyes into shadow. */
  browRidge: number;
  /** 0 flat, 1 a clear snout. */
  muzzle: number;

  // The eyes. At thumbnail size the whole eye is three pixels across, so the
  // three categorical fields below are worth more than every geometric field
  // above them put together: they read at any size and say "not human" for free.
  eyeSize: number;
  eyeSpacing: number;
  /** Negative droops, and droop reads as age or sorrow. */
  eyeSlant: number;
  /**
   * 0 wide open, 1 hooded.
   *
   * The authority's original guidance was that a wide lid "reads as alarm on
   * most faces". **That is corrected, and the corrected version is the useful
   * one: alarm comes from a *raised brow*, not from a wide lid.** Under a level
   * or heavy brow the widest possible eye reads as *staring* rather than
   * frightened — which is what the Cog-Warrens' Underwright wanted, at `lid`
   * 0.04 on `eyeSize` 1.50 with `browRidge` 0.85.
   *
   * So a wide lid is safe on its own and only wants checking when the brow is
   * also up. Established by rendering it rather than by reasoning about it.
   */
  lid: number;
  pupil: 'round' | 'slit' | 'goat' | 'pinpoint' | 'none';
  sclera: 'white' | 'dark' | 'none';
  glow: number;
  iris: readonly number[];

  /**
   * Must break the outline rather than sit inside it, or nobody sees it.
   *
   * The highest-value-per-unit field at rail size — 46px, dimmed, stacked
   * against a shared left edge — because it is one of the few things that
   * changes the *outline* rather than the modelling inside it.
   *
   * `lobed` was added for one house and is exercised by exactly one: a broad
   * rounded paddle rather than a point. It exists because four elven races
   * stacked as eight consecutive rail rows read as a wall of near-identical
   * pointed ovals, and round-against-pointed is the strongest outline contrast
   * available *inside one kin* — which is the only kind that survives a shared
   * left edge. See the Crimson Assize.
   */
  ear: 'human' | 'long' | 'swept' | 'lobed' | 'batwing' | 'round' | 'tufted' | 'fin' | 'cropped' | 'none';
  earLength: number;

  mouthWidth: number;
  /** 0 is no lip at all, which is correct for beaks, bone and chitin. */
  lip: number;
  tusk: 'none' | 'lower' | 'upper' | 'boar';
  tuskSize: number;

  /** Base, shade, deep. A single colour is a doll however good the geometry is. */
  skin: readonly [base: number, shade: number, deep: number];
  /** A few marks over the ramp. Never a repeating pattern. */
  hide: 'smooth' | 'scaled' | 'warty' | 'furred' | 'chitin' | 'bare' | 'wrapped';
  hairGrowth: 'full' | 'crest' | 'patchy' | 'none';
  hair: readonly number[];

  /** Anything sharp above the hairline reads as horns. Commit or use `none`. */
  crest: 'none' | 'horn-pair' | 'horn-swept' | 'horn-ram' | 'antler' | 'frill' | 'quill' | 'bone-plate';
  crestSize: number;

  /**
   * How much of the portrait this people's head takes — the best lever in the
   * record and the one whose intent cannot be inferred from anything else.
   * A roster that leaves it at 1 throughout has thrown away its identity before
   * a line is drawn.
   */
  frameScale: number;
  neck: number;
  shoulder: number;
}

/** The reference. A race entry is a spread over this plus the fields that differ. */
export const HUMAN_BASELINE: RaceFace = {
  id: 'human',
  cranium: 1,
  temple: 1,
  cheek: 1,
  cheekHeight: 0.5,
  jaw: 1,
  chin: 1,
  faceLength: 1,
  browRidge: 1,
  muzzle: 0,
  eyeSize: 1,
  eyeSpacing: 1,
  eyeSlant: 1,
  lid: 0.22,
  pupil: 'round',
  sclera: 'white',
  glow: 0,
  iris: [0x5f4a32, 0x4a5a48, 0x3d4a5c],
  ear: 'human',
  earLength: 1,
  mouthWidth: 1,
  lip: 1,
  tusk: 'none',
  tuskSize: 0,
  skin: [0xcaa98a, 0x9b7c60, 0x74533d],
  hide: 'smooth',
  hairGrowth: 'full',
  hair: [0x2b241d, 0x50412c, 0x8b7d64, 0x6a5238],
  crest: 'none',
  crestSize: 0,
  frameScale: 1,
  neck: 1,
  shoulder: 1,
};

/* ── Layer two: the kingdom, and its leader ──────────────────────────────── */

/**
 * A house's look. Where two kingdoms of one people become two peoples.
 *
 * Nothing here touches the skull, and that is the point: the goblins of one
 * kingdom and the goblins of the next have identical bone and are told apart
 * entirely by what has been done to it.
 */
export interface HouseLook {
  id: string;
  /** The `RaceFace.id` this house is drawn on. Several houses share one. */
  race: string;
  leader: string;

  /** The one saturated colour. Lights the niche; appears on at most three things. */
  accent: number;
  accentDark: number;

  /**
   * `hood` was removed from the authority (`src/ui/racefaces.ts`) and from here
   * with it, and the reason is worth keeping where somebody adding a crown will
   * read it: `coif`, `hood` and `veil` are three shapes for one read — cloth
   * enclosing the head — and `hood` is specifically the shape this repository
   * has twice had to take off the Huntmaster for reading as a cowl at thumbnail
   * size, and a third time when his fringe accidentally rebuilt it.
   *
   * It had also never been drawn: `crownArt` had no case for it and fell through
   * to the default, which is `antler-rack`. The one house wearing a hood would
   * have rendered in a rack of antlers. A vocabulary value with no case behind it
   * is worse than a missing feature, because it looks supported.
   *
   * A coif is fitted and shows a face opening; a veil hangs loose past the jaw.
   * Reach for one of those.
   */
  crown:
    | 'bare'
    | 'circlet'
    | 'crown-heavy'
    | 'mitre'
    | 'coif'
    | 'veil'
    | 'mask'
    | 'helm-open'
    | 'helm-closed'
    | 'helm-horned'
    | 'wreath'
    | 'antler-rack';
  grooming: 'cropped' | 'long' | 'braided' | 'topknot' | 'shaved' | 'shaved-marked' | 'wild' | 'bald' | 'tonsure';
  beard: 'none' | 'stubble' | 'short' | 'forked' | 'long' | 'moustache' | 'plaited';

  /** The material language of the metalwork. What makes unrelated shapes one people. */
  material: 'iron' | 'steel' | 'gold' | 'bronze' | 'bone' | 'coral' | 'lacquer' | 'rag' | 'glass' | 'wood' | 'stone';

  /** Two fifths of the picture's area, and usually an afterthought. */
  mantle: 'gorget' | 'pauldrons' | 'pelt' | 'robe' | 'chasuble' | 'scale' | 'harness' | 'bare';

  bearing: {
    /** Degrees; about ±8 is the useful range. */
    tilt: number;
    gaze: 'open' | 'narrow' | 'shut' | 'blind' | 'aside';
    /** Corners of the mouth. Positive up, negative down. */
    mouth: number;
  };

  /** One asymmetric thing that is this leader's alone. A race must not have one. */
  mark: 'none' | 'scar' | 'blind-eye' | 'brand' | 'broken-tusk' | 'warpaint' | 'tattoo' | 'burn';
  /** 0 young, 1 old. */
  age: number;
}

/**
 * How many of the five identity levers two houses differ in. The rule is three:
 * one is a recolour, two is a variant, three is a different person.
 *
 * Stated as a function so `tests/races.test.ts` can assert it over the whole
 * roster rather than trusting that eleven pairs were all checked by eye.
 */
export function housesDiffer(a: HouseLook, b: HouseLook): number {
  let n = 0;
  if (a.crown !== b.crown) n++;
  if (a.grooming !== b.grooming) n++;
  if (a.material !== b.material) n++;
  if (a.mantle !== b.mantle) n++;
  if (a.bearing.gaze !== b.bearing.gaze || Math.abs(a.bearing.tilt - b.bearing.tilt) >= 5) n++;
  return n;
}
